import type { LatLngBounds } from 'leaflet';
import { pipe, catchError, filter, debounce } from 'overmind';

import type { Context } from '@/overmind';
import type {
  ImageSize,
  LayerId
} from '@/overmind/effects/adapters/baseadapter';
import { getRequestAborter } from '@/overmind/effects/requestAborter';
import { UnexpectedStateError, AbortError } from '@/overmind/errors';

const { switchToNewRequest, completeRequestOperator } =
  getRequestAborter('loadMapImage');

export type GetMapImageActionBody = {
  bounds: LatLngBounds;
  size: ImageSize;
  orderedLayerIds: LayerId[];
};

export const loadMapImage = pipe(
  filter(({ state }, { bounds }) => {
    const isDuplicatedAction = state.arcgis.mapImage
      .matches('LOADING')
      ?.actionBody?.equals(bounds);
    return !isDuplicatedAction;
  }),
  debounce<GetMapImageActionBody>(50),
  async (
    { state, effects }: Context,
    { bounds, size, orderedLayerIds }: GetMapImageActionBody
  ) => {
    const abortController = switchToNewRequest();
    state.arcgis.mapImage.send('TRIGGER_LOADING', bounds);
    const adapter = effects.arcgisMapAdapter.getAdapter();
    if (adapter) {
      return adapter.getBackgroundMapImage(
        bounds,
        size,
        orderedLayerIds,
        abortController
      );
    }

    throw new UnexpectedStateError();
  },
  ({ state }: Context, mapImage) => {
    state.arcgis.mapImage.send('FETCH_SUCCESS', mapImage);
  },
  catchError(({ state }: Context, error) => {
    if (
      !(error instanceof UnexpectedStateError || error instanceof AbortError)
    ) {
      state.arcgis.mapImage.send('FETCH_ERROR', error.message);
    }
  }),
  completeRequestOperator()
);

export const resetMapImageState = pipe(async ({ state }: Context) => {
  state.arcgis.mapImage.send('UNLOAD');
});
